import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FolderIcon from "@mui/icons-material/Folder";
import FactoryIcon from "@mui/icons-material/Factory";
import { useMemo, useState } from "react";
import { NucToast } from "src/reusable/components/Toast";
import DatasetSelectorModal from "src/reusable/Chat/PromptInput/DatasetSelectorModal";
import type { Dataset as SelectorDataset } from "src/reusable/Chat/PromptInput/ArtifactSelectorModal";
import type { WorkflowReferenceRole } from "src/views/products/workflow/services/WorkflowProductService";
import {
  useAddFfnReferenceDocument,
  useCreateFfnPlant,
  useFfnPlants,
} from "../services/FfnService";
import { useFfnStore } from "../store/FfnStore";

/**
 * Splash card selecting WHICH plant the 50.59 activity concerns, with the
 * minimal management a fresh install needs: registering a plant and linking
 * reference datasets (UFSAR, tech specs, ...) under the product's declared
 * roles. Full library management stays out of scope — these controls exist
 * because without them the only path is driving the agent's tools by hand.
 */
const FfnPlantCard = ({
  referenceRoles,
}: {
  referenceRoles: WorkflowReferenceRole[];
}) => {
  const { data: plants = [], isLoading } = useFfnPlants();
  const { selectedPlantId, setSelectedPlantId } = useFfnStore();
  const { mutateAsync: createPlant, isLoading: isCreatingPlant } =
    useCreateFfnPlant();
  const { mutateAsync: linkDataset, isLoading: isLinking } =
    useAddFfnReferenceDocument();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newPlantName, setNewPlantName] = useState("");
  const [newPlantDescription, setNewPlantDescription] = useState("");

  const [linkRole, setLinkRole] = useState<string>("");
  const [isDatasetModalOpen, setIsDatasetModalOpen] = useState(false);

  const selectedPlant = useMemo(
    () => plants.find((p) => p.id === selectedPlantId) ?? null,
    [plants, selectedPlantId],
  );

  const handleCreatePlant = async () => {
    if (!newPlantName.trim()) {
      NucToast({ message: "Enter a plant name", type: "error" });
      return;
    }
    try {
      const plant = await createPlant({
        name: newPlantName.trim(),
        description: newPlantDescription.trim() || undefined,
      });
      setSelectedPlantId(plant.id);
      setIsCreateOpen(false);
      setNewPlantName("");
      setNewPlantDescription("");
    } catch (error: any) {
      NucToast({
        message: error?.response?.data?.detail || "Failed to create plant",
        type: "error",
      });
    }
  };

  const handleLinkDatasets = async (datasets: SelectorDataset[]) => {
    if (!selectedPlant || !linkRole) return;
    // Nothing rejects a repeat link, so the same dataset can be attached to a
    // role twice and render as two identical chips.
    const linkedForRole = new Set(
      selectedPlant.reference_documents
        .filter((doc) => doc.role === linkRole)
        .map((doc) => doc.dataset_id),
    );
    const toLink = datasets.filter((d) => !linkedForRole.has(d.id));
    const skipped = datasets.length - toLink.length;

    if (toLink.length === 0) {
      NucToast({
        message: `Already linked as ${linkRole}.`,
        type: "info",
      });
      setLinkRole("");
      return;
    }

    let linked = 0;
    try {
      for (const dataset of toLink) {
        await linkDataset({
          plantId: selectedPlant.id,
          role: linkRole,
          datasetId: dataset.id,
        });
        linked += 1;
      }
      NucToast({
        message:
          `Linked ${linked} dataset(s) as ${linkRole}` +
          (skipped > 0 ? ` (${skipped} already linked)` : ""),
        type: "success",
      });
    } catch (error: any) {
      // Links are sequential, so say how far it got — the earlier ones stay.
      const detail =
        error?.response?.data?.detail || "Failed to link reference dataset";
      NucToast({
        message:
          linked > 0
            ? `${detail}. ${linked} of ${toLink.length} linked before the failure.`
            : detail,
        type: "error",
      });
    } finally {
      setLinkRole("");
    }
  };

  return (
    <Card elevation={2}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: "0.875rem",
              bgcolor: "primary.main",
            }}
          >
            1
          </Avatar>
          <FactoryIcon color="primary" />
          <Typography variant="h6" component="h2">
            Plant
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            select
            fullWidth
            size="small"
            label={isLoading ? "Loading plants…" : "Select plant"}
            value={selectedPlantId ?? ""}
            onChange={(e) =>
              setSelectedPlantId(
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
            data-testid="ffn-select-plant"
          >
            {plants.map((plant) => (
              <MenuItem key={plant.id} value={plant.id}>
                {plant.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateOpen(true)}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            data-testid="ffn-btn-add-plant"
          >
            Add plant
          </Button>
        </Stack>

        {selectedPlant && (
          <Box mt={2}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              Reference library
            </Typography>
            {selectedPlant.reference_documents.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {selectedPlant.reference_documents.map((doc) => (
                  <Chip
                    key={doc.id}
                    size="small"
                    label={`${doc.role}: ${doc.dataset_name ?? `dataset ${doc.dataset_id}`}`}
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No reference documents linked yet. The agent needs at least a
                UFSAR to screen against.
              </Typography>
            )}
            <Stack direction="row" spacing={1} alignItems="center" mt={1.5}>
              <TextField
                select
                size="small"
                label="Role"
                value={linkRole}
                onChange={(e) => setLinkRole(e.target.value)}
                sx={{ minWidth: 220 }}
                data-testid="ffn-select-reference-role"
              >
                {referenceRoles.map((role) => (
                  <MenuItem key={role.role_key} value={role.role_key}>
                    {role.display_name}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                startIcon={<FolderIcon />}
                disabled={!linkRole || isLinking}
                onClick={() => setIsDatasetModalOpen(true)}
                sx={{ textTransform: "none" }}
                data-testid="ffn-btn-link-reference-dataset"
              >
                Link reference dataset
              </Button>
            </Stack>
          </Box>
        )}

        <Dialog
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Register plant</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField
                autoFocus
                fullWidth
                label="Plant name"
                value={newPlantName}
                onChange={(e) => setNewPlantName(e.target.value)}
                data-testid="ffn-input-plant-name"
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Description (optional)"
                value={newPlantDescription}
                onChange={(e) => setNewPlantDescription(e.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreatePlant}
              disabled={isCreatingPlant}
              data-testid="ffn-btn-create-plant"
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        <DatasetSelectorModal
          open={isDatasetModalOpen}
          onClose={() => setIsDatasetModalOpen(false)}
          onConfirm={handleLinkDatasets}
          defaultSelectedDatasets={[]}
        />
      </CardContent>
    </Card>
  );
};

export default FfnPlantCard;
